import React, { useState } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import { ArrowRight, CheckCircle2, AlertTriangle, Info, ChevronDown, Link2, MessageCircle } from "lucide-react";
import { toast } from "sonner";
import { PRODUCT_PAGES, GUARD_MODULES } from "../data/products";
import { PRODUCT_SCENES } from "../data/productScenes";
import { JourneyBar } from "../components/JourneyBar";
import Product3DVisual from "../components/Product3DVisual";
import { WATTZAP_OS_START_URL, buildWattzapOSUrl, openEnergyScanPortal } from "../lib/wattzapOS";
import { useT } from "../i18n/LanguageContext";

function Bullet({ children }) {
  return (
    <li className="flex items-start gap-3">
      <CheckCircle2 className="text-[#21F59B] mt-0.5 shrink-0" size={18} />
      <span className="text-slate-700 text-sm leading-relaxed">{children}</span>
    </li>
  );
}

function ListCard({ title, items }) {
  const t = useT();
  return (
    <div className="bg-white border border-slate-100 rounded-2xl p-7 shadow-card-soft">
      <h3 className="font-heading text-xl text-graphite">{t(title)}</h3>
      <ul className={`mt-5 grid gap-x-8 gap-y-3 ${items.length > 5 ? "sm:grid-cols-2" : ""}`}>
        {items.map((it) => <Bullet key={it}>{t(it)}</Bullet>)}
      </ul>
    </div>
  );
}

function Block({ block }) {
  const t = useT();
  switch (block.type) {
    case "heading":
      return (
        <div className="pt-4">
          <h2 className="font-heading text-2xl lg:text-3xl text-graphite">{t(block.title)}</h2>
          {block.body && <p className="mt-2 text-slate-600 max-w-2xl">{t(block.body)}</p>}
        </div>
      );
    case "list":
      return <ListCard title={block.title} items={block.items} />;
    case "columns":
      return (
        <div className="grid md:grid-cols-2 gap-6 items-start">
          {block.cols.map((c) => <ListCard key={c.title} title={c.title} items={c.items} />)}
        </div>
      );
    case "chips":
      return (
        <div className="bg-white border border-slate-100 rounded-2xl p-7 shadow-card-soft">
          <h3 className="font-heading text-xl text-graphite">{t(block.title)}</h3>
          <div className="mt-5 flex flex-wrap gap-2">
            {block.items.map((it) => (
              <span key={it} className="px-3.5 py-1.5 rounded-full text-xs font-medium bg-mist border border-slate-200 text-slate-700">{t(it)}</span>
            ))}
          </div>
        </div>
      );
    case "steps":
      return (
        <div className="bg-white border border-slate-100 rounded-2xl p-7 shadow-card-soft">
          <h3 className="font-heading text-xl text-graphite">{t(block.title)}</h3>
          <div className="mt-6 grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {block.items.map((s, i) => (
              <div key={s.title}>
                <div className="text-xs font-medium tracking-widest uppercase text-[#00D4FF]">{t(s.label)}</div>
                <div className="mt-1.5 flex items-center gap-2">
                  <span className="w-7 h-7 rounded-full bg-wz-gradient text-[#071A2D] text-xs font-bold flex items-center justify-center shrink-0">{i + 1}</span>
                  <span className="font-heading text-base text-graphite">{t(s.title)}</span>
                </div>
                {s.body && <p className="mt-2 text-sm text-slate-600 leading-relaxed">{t(s.body)}</p>}
                {s.items && (
                  <ul className="mt-2 space-y-1.5">
                    {s.items.map((it) => <li key={it} className="text-sm text-slate-600">• {t(it)}</li>)}
                  </ul>
                )}
              </div>
            ))}
          </div>
        </div>
      );
    case "note": {
      const warn = block.tone === "warning";
      return (
        <div className={`rounded-2xl border px-6 py-5 flex items-start gap-3 ${warn ? "bg-[#FFC857]/10 border-[#FFC857]/50" : "bg-[#00D4FF]/5 border-[#00D4FF]/30"}`}>
          {warn ? <AlertTriangle size={18} className="text-[#b98a2a] mt-0.5 shrink-0" /> : <Info size={18} className="text-[#0092b0] mt-0.5 shrink-0" />}
          <p className="text-sm text-slate-700 leading-relaxed">{t(block.body)}</p>
        </div>
      );
    }
    case "infocards":
      return (
        <div>
          <h3 className="font-heading text-xl text-graphite">{t(block.title)}</h3>
          <div className="mt-5 grid md:grid-cols-3 gap-5">
            {block.items.map((c, i) => (
              <div key={c.title} className="bg-white border border-slate-100 rounded-2xl p-6 shadow-card-soft">
                <div className="w-8 h-8 rounded-full bg-wz-gradient text-[#071A2D] text-sm font-bold flex items-center justify-center">{i + 1}</div>
                <div className="mt-4 font-heading text-base text-graphite">{t(c.title)}</div>
                <p className="mt-2 text-sm text-slate-600 leading-relaxed">{t(c.body)}</p>
              </div>
            ))}
          </div>
        </div>
      );
    case "modulecards":
      return (
        <div>
          <h3 className="font-heading text-xl text-graphite">{t(block.title)}</h3>
          <div className="mt-5 grid sm:grid-cols-2 gap-6">
            {GUARD_MODULES.map((m) => {
              const Icon = m.icon;
              return (
                <div key={m.name} data-testid={`guard-module-card-${m.name.toLowerCase().replace(/\s+/g, "-")}`} className="bg-white border border-slate-100 rounded-2xl p-7 shadow-card-soft hover:-translate-y-1 hover:shadow-cyan-glow/30 transition-all flex flex-col">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-cyan/10 to-energy/10 flex items-center justify-center text-[#00D4FF]">
                    <Icon size={22} />
                  </div>
                  <h4 className="mt-5 font-heading text-lg text-graphite">{m.name}</h4>
                  <p className="mt-2 text-sm text-slate-600 leading-relaxed">{t(m.tagline)}</p>
                  <div className="mt-auto pt-5">
                    <Link to={m.to} className="inline-flex items-center gap-2 text-sm font-medium text-[#00D4FF] hover:gap-3 transition-all">
                      {t("Explore")} {m.name} <ArrowRight size={15} />
                    </Link>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      );
    default:
      return null;
  }
}

function ShareProduct({ page }) {
  const t = useT();
  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      toast.success(t("Link copied — ready to share"));
    } catch {
      toast.error(t("Could not copy — long-press the address bar instead"));
    }
  };
  const waShare = () => {
    const text = `${page.name} — ${page.tagline}\n${window.location.href}`;
    window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, "_blank", "noreferrer");
  };
  return (
    <div className="mt-4 flex items-center gap-2">
      <span className="text-xs text-white/45 mr-1">{t("Share this product:")}</span>
      <button
        type="button"
        onClick={copyLink}
        data-testid="share-copy-link"
        className="inline-flex items-center gap-1.5 text-xs text-white/70 hover:text-white border border-white/20 hover:border-white/40 rounded-full px-3 py-1.5 transition-colors focus-visible:ring-2 focus-visible:ring-[#00D4FF] focus:outline-none"
      >
        <Link2 size={13} /> Copy link
      </button>
      <button
        type="button"
        onClick={waShare}
        data-testid="share-whatsapp"
        className="inline-flex items-center gap-1.5 text-xs text-[#21F59B] hover:brightness-110 border border-[#21F59B]/40 hover:border-[#21F59B]/70 rounded-full px-3 py-1.5 transition-colors focus-visible:ring-2 focus-visible:ring-[#00D4FF] focus:outline-none"
      >
        <MessageCircle size={13} /> WhatsApp
      </button>
    </div>
  );
}

function SeeHowItWorks({ scene }) {
  const t = useT();
  const [open, setOpen] = useState(false);
  return (
    <div className="bg-white border border-slate-100 rounded-2xl shadow-card-soft overflow-hidden">
      <button
        type="button"
        data-testid="see-how-it-works-toggle"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between px-7 py-5 text-left"
      >
        <span className="font-heading text-xl text-graphite">{t("See how it works")}</span>
        <ChevronDown size={18} className={`shrink-0 text-slate-500 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div data-testid="see-how-it-works-content" className="px-7 pb-7 space-y-6">
          <p className="text-sm text-slate-600 leading-relaxed">{scene.description}</p>
          {scene.sequence && (
            <ol className="space-y-2">
              {scene.sequence.map((s, i) => (
                <li key={s} className="flex items-start gap-3 text-sm text-slate-700">
                  <span className="w-6 h-6 rounded-full bg-wz-gradient text-[#071A2D] text-xs font-bold flex items-center justify-center shrink-0">{i + 1}</span>
                  {s}
                </li>
              ))}
            </ol>
          )}
          <div className="grid sm:grid-cols-2 gap-x-8 gap-y-4">
            {scene.hotspots.map((h) => (
              <div key={h.node}>
                <div className="text-sm font-medium text-graphite">{h.title}</div>
                <p className="mt-1 text-sm text-slate-600 leading-relaxed">{h.body}</p>
              </div>
            ))}
          </div>
          {scene.kpis && (
            <div>
              <div className="text-sm font-medium text-graphite">{t("Monitored indicators")} <span className="text-xs text-slate-400 font-normal">{t("(example only)")}</span></div>
              <div className="mt-2 flex flex-wrap gap-2">
                {scene.kpis.map((k) => (
                  <span key={k} className="px-3 py-1 rounded-full text-xs bg-mist border border-slate-200 text-slate-600">{k}</span>
                ))}
              </div>
            </div>
          )}
          <p className="text-sm font-medium text-graphite border-l-2 border-[#21F59B] pl-3">{scene.footer}</p>
        </div>
      )}
    </div>
  );
}

export default function ProductDetail() {
  const t = useT();
  const { slug, moduleSlug } = useParams();
  const key = moduleSlug ? `guard/${moduleSlug}` : slug;
  const page = PRODUCT_PAGES[key];
  if (!page) return <Navigate to="/products" replace />;
  const scene = PRODUCT_SCENES[key];

  return (
    <div data-testid="product-detail-page" key={key}>
      {/* HERO */}
      <section className="bg-[#071A2D] text-white pt-20 pb-16">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <div className={`grid gap-10 items-center ${scene ? "lg:grid-cols-2" : ""}`}>
            <div>
              <div className="text-xs uppercase tracking-[0.2em] text-[#00D4FF] mb-3">
                <Link to="/products" className="hover:text-white transition-colors" data-testid="product-breadcrumb">{t("Products")}</Link>
                {page.parent && (
                  <>
                    <span className="text-white/40"> / </span>
                    <Link to={page.parent.to} className="hover:text-white transition-colors">{page.parent.label}</Link>
                  </>
                )}
                <span className="text-white/40"> / </span>
                <span className="text-white/70">{page.name}</span>
              </div>
              <h1 data-testid="product-title" className="font-heading text-4xl sm:text-5xl lg:text-6xl max-w-3xl">{page.name}</h1>
              <p data-testid="product-tagline" className="mt-4 font-heading text-lg text-[#21F59B] max-w-2xl">{t(page.tagline)}</p>
              <p className="mt-5 text-white/75 max-w-2xl">{t(page.description)}</p>
              {page.price && (
                <div data-testid="product-price-chip" className="mt-6 inline-flex items-center gap-2.5 px-4 py-2 rounded-full border border-white/15 bg-white/[0.05]">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#FFC857]" />
                  <span className="text-sm font-heading text-white">{page.price}</span>
                </div>
              )}
              <div className="mt-8">
                {page.cta.external ? (
                  <a href={buildWattzapOSUrl()} onClick={(e) => { e.preventDefault(); openEnergyScanPortal(); }} data-testid="product-hero-cta" className="btn-gradient inline-flex items-center gap-2 px-6 py-3.5 rounded-full text-sm">
                    {t(page.cta.label)} <ArrowRight size={16} />
                  </a>
                ) : (
                  <Link to={page.cta.to} data-testid="product-hero-cta" className="btn-gradient inline-flex items-center gap-2 px-6 py-3.5 rounded-full text-sm">
                    {t(page.cta.label)} <ArrowRight size={16} />
                  </Link>
                )}
              </div>
              <ShareProduct page={page} />
            </div>
            {scene && <Product3DVisual scene={scene} testId={`product-3d-${key.replace("/", "-")}`} />}
          </div>
          <div className="mt-10">
            <div className="text-xs uppercase tracking-widest text-white/50 mb-3">{t("Where this fits in the journey")}</div>
            <JourneyBar active={page.stage} dark testId="product-journey-bar" />
          </div>
        </div>
      </section>

      {/* CONTENT BLOCKS */}
      <section className="bg-mist py-16 lg:py-20">
        <div className="max-w-5xl mx-auto px-5 lg:px-8 space-y-8">
          {scene && <SeeHowItWorks scene={scene} />}
          {page.blocks.map((b, i) => <Block key={i} block={b} />)}
        </div>
      </section>

      {/* BOTTOM CTA */}
      <section className="bg-[#071A2D] text-white py-16">
        <div className="max-w-4xl mx-auto px-5 lg:px-8 text-center">
          <h3 className="font-heading text-3xl">{t("Every recommendation starts with your data.")}</h3>
          {page.cta.external ? (
            <a href={buildWattzapOSUrl()} onClick={(e) => { e.preventDefault(); openEnergyScanPortal(); }} data-testid="product-bottom-cta" className="mt-6 inline-flex items-center gap-2 btn-gradient px-6 py-3 rounded-full text-sm">
              {t(page.cta.label)} <ArrowRight size={16} />
            </a>
          ) : (
            <Link to={page.cta.to} data-testid="product-bottom-cta" className="mt-6 inline-flex items-center gap-2 btn-gradient px-6 py-3 rounded-full text-sm">
              {t(page.cta.label)} <ArrowRight size={16} />
            </Link>
          )}
        </div>
      </section>
    </div>
  );
}
